import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateAppointment } from '../hooks/useApi';
import { ServiceSummary, AppointmentCreate } from '../types/api';

interface Props {
  clinicId: string;
  services: ServiceSummary[];
}

export default function AppointmentForm({ clinicId, services }: Props) {
  const navigate = useNavigate();
  const mutation = useCreateAppointment();
  const [error, setError] = useState('');

  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = new FormData(e.currentTarget);

    const data: AppointmentCreate = {
      clinic_id: clinicId,
      contact_name: form.get('contact_name') as string,
      contact_phone: form.get('contact_phone') as string,
      contact_email: (form.get('contact_email') as string) || undefined,
      preferred_date: (form.get('preferred_date') as string) || undefined,
      preferred_time_slot: (form.get('preferred_time_slot') as AppointmentCreate['preferred_time_slot']) || 'any',
      service_id: (form.get('service_id') as string) || undefined,
      reason: (form.get('reason') as string) || undefined,
    };

    try {
      const result = await mutation.mutateAsync(data);
      navigate(`/status/${result.verification_token}`);
    } catch {
      setError('Failed to submit appointment. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div>
        <label className="label" htmlFor="contact_name">Full Name *</label>
        <input id="contact_name" name="contact_name" required className="input" placeholder="Your full name" />
      </div>

      <div>
        <label className="label" htmlFor="contact_phone">Phone Number *</label>
        <input id="contact_phone" name="contact_phone" type="tel" required className="input" placeholder="(242) 000-0000" />
      </div>

      <div>
        <label className="label" htmlFor="contact_email">Email (optional)</label>
        <input id="contact_email" name="contact_email" type="email" className="input" placeholder="you@example.com" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label" htmlFor="preferred_date">Preferred Date</label>
          <input id="preferred_date" name="preferred_date" type="date" min={today} className="input" />
        </div>
        <div>
          <label className="label" htmlFor="preferred_time_slot">Time Slot</label>
          <select id="preferred_time_slot" name="preferred_time_slot" className="input">
            <option value="any">Any Time</option>
            <option value="morning">Morning</option>
            <option value="afternoon">Afternoon</option>
          </select>
        </div>
      </div>

      {services.length > 0 && (
        <div>
          <label className="label" htmlFor="service_id">Service (optional)</label>
          <select id="service_id" name="service_id" className="input">
            <option value="">General Visit</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="label" htmlFor="reason">Reason for Visit (optional)</label>
        <textarea id="reason" name="reason" rows={3} className="input resize-none" placeholder="Brief description..." />
      </div>

      <button type="submit" disabled={mutation.isPending} className="btn btn-primary w-full">
        {mutation.isPending ? 'Submitting...' : 'Request Appointment'}
      </button>
    </form>
  );
}
